/**
 * Resolve the product type for cart items using explicit metadata when
 * available and sensible fallbacks otherwise.
 *
 * @param {Record<string, any>} product
 * @returns {"pet_product" | "merchandise"}
 */
export const resolveProductType = (product) => {
  if (!product) return "pet_product";

  const explicitType = product.product_type;
  if (explicitType === "merchandise" || explicitType === "pet_product") {
    return explicitType;
  }

  const productId =
    (typeof product.product_id === "number"
      ? product.product_id.toString()
      : product.product_id) ||
    (typeof product.id === "number" ? product.id.toString() : product.id) ||
    "";

  if (typeof productId === "string") {
    const normalizedId = productId.toLowerCase();
    if (normalizedId.includes("merch")) {
      return "merchandise";
    }
  }

  if (product.internal_reference_sku) {
    return "pet_product";
  }

  const rawPrice =
    product.variant_price ??
    product.first_price ??
    product.sales_price ??
    product.price;

  const priceNumber = parseFloat(rawPrice);

  if (!Number.isNaN(priceNumber)) {
    const asString = rawPrice?.toString() ?? "";
    const looksLikeCents =
      Number.isInteger(priceNumber) && !asString.includes(".");

    if (looksLikeCents && priceNumber > 99) {
      return "merchandise";
    }
  }

  return "pet_product";
};

/**
 * Normalize product data prior to storing in the cart to guarantee the
 * presence of a product_type and avoid mutating the original payload.
 *
 * @param {Record<string, any>} product
 * @returns {Record<string, any>}
 */
export const prepareCartProduct = (product) => {
  if (!product || typeof product !== "object") {
    return product;
  }

  const productType = resolveProductType(product);

  return {
    ...product,
    product_type: productType,
  };
};
